import { Form, Formik } from "formik";
import type {
  AssetPosition,
  AssetPositionConfigurationInput,
  BasicAuthCredentials,
  PublicKeyCredentials,
  SFTPReceiverParams,
} from "kubb";
import { sftpParserTypeEnum } from "kubb";
import type React from "react";
import { ErrorBanner } from "../../../components/Error/ErrorBanner";
import { ButtonGroupField } from "../../../components/Forms/ButtonGroup";
import NewTextFormComponent from "../../../components/Forms/NewTextFormComponent";
import { SelectComboField } from "../../../components/Forms/SelectCombo/SelectCombo";
import BlockSpinner from "../../../components/Spinners/BlockSpinner";
import { Button } from "../../../components/Theme/button";
import { Heading } from "../../../components/Theme/heading";
import DisplayIfUserType from "../../../components/utils/DisplayIfUserType/DisplayIfUserType";
import { useUpdateAssetPosition } from "../../../hooks/updateEntity";
import useUserHasRoles from "../../../hooks/user/getCurrentUserRole";
import { NONE_PARSER_TYPE } from "./SFTPReceiverIntegrationForm";

interface SFTPCredentialsDisplayProps {
  assetPosition: AssetPosition;
  refresh?: () => void;
}

interface FormValues {
  sftpServer: string;
  serverFingerprints: string;
  username: string;
  authenticationType: "public_key" | "basic_auth";
  password: string;
  publicKey: string;
  parserType: string;
}

type SFTPCredentials = PublicKeyCredentials | BasicAuthCredentials;

function getSFTPCredentials(
  assetPosition: AssetPosition,
): SFTPCredentials | undefined {
  const integration = assetPosition.configuration?.integration;
  if (
    integration &&
    "sftp_credentials" in integration &&
    integration.sftp_credentials
  ) {
    return integration.sftp_credentials as SFTPCredentials;
  }
  return undefined;
}

function getParserType(assetPosition: AssetPosition): string {
  const integration = assetPosition.configuration
    ?.integration as SFTPReceiverParams | null;
  // Convert null/undefined to sentinel value for form display
  return integration?.parser_type || NONE_PARSER_TYPE;
}

export default function SFTPCredentialsDisplay(
  props: SFTPCredentialsDisplayProps,
): React.ReactElement {
  const { assetPosition, refresh } = props;
  const canEdit = useUserHasRoles(["editor", "admin", "superadmin"]);
  const { update, loading, error } = useUpdateAssetPosition(assetPosition);

  const credentials = getSFTPCredentials(assetPosition);
  const authType = credentials?.type || "public_key";
  const integrationType =
    assetPosition.configuration?.integration?.integration_type;
  const isSftpReceiver = integrationType === "sftp_receiver";

  const fingerprints = [
    import.meta.env.VITE_SFTP_FINGERPRINT_RSA &&
      `RSA: ${import.meta.env.VITE_SFTP_FINGERPRINT_RSA}`,
    import.meta.env.VITE_SFTP_FINGERPRINT_ECDSA &&
      `ECDSA: ${import.meta.env.VITE_SFTP_FINGERPRINT_ECDSA}`,
  ]
    .filter(Boolean)
    .join("\n");

  const initialValues: FormValues = {
    sftpServer: import.meta.env.VITE_SFTP_SERVER || "",
    serverFingerprints: fingerprints,
    username: credentials?.username || assetPosition.asset_position_id || "",
    authenticationType: authType as "public_key" | "basic_auth",
    password:
      authType === "basic_auth"
        ? (credentials as BasicAuthCredentials)?.password || ""
        : "",
    publicKey:
      authType === "public_key"
        ? (credentials as PublicKeyCredentials)?.public_key || ""
        : "",
    parserType: getParserType(assetPosition),
  };

  const handleSubmit = (
    values: FormValues,
    { setSubmitting }: { setSubmitting: (isSubmitting: boolean) => void },
  ) => {
    setSubmitting(true);

    const newCredentials: SFTPCredentials =
      values.authenticationType === "basic_auth"
        ? ({
            type: "basic_auth",
            password: values.password,
          } as BasicAuthCredentials)
        : ({
            type: "public_key",
            public_key: values.publicKey,
          } as PublicKeyCredentials);

    // Type assertion needed because kubb-generated types don't yet include BasicAuthCredentials
    // in the sftp_credentials union. The backend supports both credential types.
    const newConfiguration = {
      ...assetPosition.configuration,
      integration: {
        ...assetPosition.configuration?.integration,
        sftp_credentials: newCredentials,
        // Only include parser_type for sftp_receiver integrations
        ...(isSftpReceiver && {
          parser_type:
            values.parserType === NONE_PARSER_TYPE ? null : values.parserType,
        }),
      },
    } as AssetPositionConfigurationInput;

    update({
      ...assetPosition,
      configuration: newConfiguration,
    })
      .then(() => {
        refresh?.();
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  return (
    <>
      <Heading>SFTP Credentials</Heading>
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        enableReinitialize={true}
      >
        {({ values, isSubmitting, dirty }) => {
          const isBasicAuth = values.authenticationType === "basic_auth";

          return (
            <Form>
              <BlockSpinner loading={loading}>
                {/* Parser type selector - only for sftp_receiver and editors */}
                {isSftpReceiver && (
                  <DisplayIfUserType userTypes="editor">
                    <SelectComboField
                      fieldName="parserType"
                      label={{ label: "Parser Type" }}
                      options={[
                        { value: NONE_PARSER_TYPE, label: "None" },
                        {
                          value: sftpParserTypeEnum.kamstrup_ready,
                          label: "Kamstrup Ready",
                        },
                        {
                          value: sftpParserTypeEnum.bring_energy_bms,
                          label: "Bring Energy BMS",
                        },
                      ]}
                    />
                  </DisplayIfUserType>
                )}

                {/* Auth type switcher - only for editors */}
                <DisplayIfUserType userTypes="editor">
                  <ButtonGroupField
                    fieldName="authenticationType"
                    label={{ label: "Authentication Type" }}
                    options={[
                      { value: "public_key", label: "SSH Key" },
                      { value: "basic_auth", label: "Password" },
                    ]}
                  />
                </DisplayIfUserType>

                {/* SFTP Server - always read-only */}
                <NewTextFormComponent
                  fieldName="sftpServer"
                  label={{ label: "SFTP Server" }}
                  staticValue
                  disabled
                  clipboard
                />

                {/* Server fingerprints - read-only reference for verification */}
                {(import.meta.env.VITE_SFTP_FINGERPRINT_RSA ||
                  import.meta.env.VITE_SFTP_FINGERPRINT_ECDSA) && (
                  <NewTextFormComponent
                    fieldName="serverFingerprints"
                    label={{ label: "Server Fingerprints" }}
                    staticValue
                    disabled
                    clipboard
                  />
                )}

                {/* Username - always read-only (set by backend) */}
                <NewTextFormComponent
                  fieldName="username"
                  label={{ label: "Username" }}
                  staticValue
                  disabled
                  clipboard
                  autoComplete="off"
                />

                {/* Credentials - read-only for viewers, editable for editors */}
                {isBasicAuth ? (
                  canEdit ? (
                    <NewTextFormComponent
                      fieldName="password"
                      label={{ label: "Password" }}
                      type="password"
                      autoComplete="off"
                    />
                  ) : (
                    <NewTextFormComponent
                      fieldName="password"
                      label={{ label: "Password" }}
                      staticValue
                      disabled
                      clipboard
                      autoComplete="off"
                    />
                  )
                ) : canEdit ? (
                  <NewTextFormComponent
                    fieldName="publicKey"
                    label={{ label: "SSH Public Key" }}
                    type="textarea"
                    autoComplete="off"
                  />
                ) : (
                  <NewTextFormComponent
                    fieldName="publicKey"
                    label={{ label: "SSH Public Key" }}
                    staticValue
                    disabled
                    clipboard
                    autoComplete="off"
                  />
                )}

                {/* Submit button - only for editors */}
                <DisplayIfUserType userTypes="editor">
                  <Button
                    type="submit"
                    color="brandLight"
                    disabled={isSubmitting || !dirty}
                  >
                    Update Credentials
                  </Button>
                </DisplayIfUserType>

                <ErrorBanner error={error} />
              </BlockSpinner>
            </Form>
          );
        }}
      </Formik>
    </>
  );
}
